import { useContext, useEffect, useRef, useState } from "react";
import * as L from "./layout.styles";
import styles from "./index.module.sass";
import { IChoiceGroupOption, TextField, ChoiceGroup, Stack, SpinnerSize, Spinner, DetailsList, SelectionMode, MessageBar, MessageBarType, Link } from "@fluentui/react";
import { Redirect, RouteComponentProps, useHistory } from "react-router-dom";
import { InvestmentFundFinder } from "./components/InvestmentFundFinder";
import { DistributorFinder } from "./components/DistributorFinder";
import { ThemeContext } from "@fluentui/react-theme-provider";
import Auth from "../../Auth/Auth";
import OrderService, { OrderType } from "../../services/OrderService";
import fundService, { TransferEstimationDates, TransferSimulationManagerInfo } from "../../services/FundService";
import { useForm, Controller } from "react-hook-form";
import { UserType } from "@/services/UserService";
import React from "react";
import DocumentTitle from "react-document-title";
import { TransferTimeLine } from "./components/TransferTimeLine";
import DocPreLogService, { checkAllDocsIncluded, DocPreLogs } from "@/services/DocPreLogService";
import PromotionsService, { ValidateISINResponse } from "@/services/PromotionsService";
import moment from "moment";
import { currencyFormatter, decimalFormatter, roundToDecimalPlaces } from "@/utils/numberFormatter";
import * as T from "./TransferFormScreenStyles";
import { getUsableMainIdentityId, UnusableMainIdentityOrderMessage } from "@/utils/mainUserIdentityRules";

interface RouteParams { }
interface ITransferFormScreenProps extends RouteComponentProps<RouteParams> {}

const amountTypeOptions: IChoiceGroupOption[] = [
    { key: "0", text: "Por importe", value: "amount" },
    { key: "1", text: "Por participaciones", value: "titles" },
    { key: "2", text: "Total", value: "total" },
];

interface filters
{
    sourceIsin: "";
    sourceName: "";
}

type TDocumentsColumnsType =
{
    key: string;
    name: string;
    fieldName: string;
    minWidth: number;
    maxWidth?: number;
};

export const TransferFormScreen: React.FunctionComponent<ITransferFormScreenProps> = (props, context) =>
{
    const theme = useContext(ThemeContext);
    let activePlan = Auth.getActivePlan();
    const {register, handleSubmit, watch, setError, formState: { errors }, control, setValue } = useForm();
    const [sending, setSending] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string>();
    const [simulation, setSimulation] = useState<boolean>(false);
    const [hasChangedIsin, setHasChangedIsin] = useState<boolean>(false);
    const [hasChangedExternalManager, setHasChangedExternalManager] = useState<boolean>(false);
    const [simulationAvailable, setSimulationAvailable] = useState<boolean>(false);
    const [transferDates, setTransferDates] = useState<TransferEstimationDates>(
    {
        compraLiquidacion: "",
        compraValor: "",
        finalProceso: "",
        ventaLiquidacion: "",
        ventaValor: "",
    });
    const history = useHistory();
    const mounted = useRef(false);
    let userData: UserType = Auth.getUserProfile();
    const watchAmountType = watch("amountType", "");
    const outdated = Auth.isLastPlanOutdated();
    const cancelled = Auth.isActivePlanCancelled();
    const activeManagementPlan = Auth.isActiveManagementPlan();
    const [filters, setFilters] = useState<filters>(
    {
        sourceIsin: "",
        sourceName: "",
    });
    const [simulationLoading, setSimulationLoading] = useState<boolean>(false);
    const [originTransferSimulationManagerInfo, setOriginTransferSimulationManagerInfo] = useState<TransferSimulationManagerInfo[]>([]);
    const [targetTransferSimulationManagerInfo, setTargetTransferSimulationManagerInfo] = useState<TransferSimulationManagerInfo[]>([]);
    const [docPreLogs, setDocPreLogs] = useState<DocPreLogs[]>();
    const [loadingDocPreLogs, setLoadingDocPreLogs] = useState<boolean>(false);
    const [allDocumentsReaded, setAllDocumentsReaded] = useState<boolean>(true);
    const [targetFundDocuments, setTargetFundDocuments] = useState<any[]>([]);
    const [activePromotions, setActivePromotions] = useState<ValidateISINResponse | null>(null);
    const [loadingPromotions, setLoadingPromotions] = useState<boolean>(false);
    const [targetFundData, setTargetFundData] = useState<{nav: number; ongoingCharges: number;} | null>(null);
    const [targetFundDataLoading, setTargetFundDataLoading] = useState<boolean>(false);
    const [sourceFundData, setSourceFundData] = useState<{ nav: number } | null>(null);
    const [sourceFundDataLoading, setSourceFundDataLoading] = useState<boolean>(false);

    const documentsColumns: TDocumentsColumnsType[] =
    [
        {
            key: "name",
            name: "Fichero",
            fieldName: "name",
            minWidth: 200,
            maxWidth: 300,
        },
        {
            key: "link",
            name: "Acción",
            fieldName: "url",
            minWidth: 120,
            maxWidth: 120,
        },
        {
            key: "dateClick",
            name: "Fecha de lectura",
            fieldName: "dateClick",
            minWidth: 180,
        },
    ];

    const sourceIsin = watch("sourceFund")?.isin ?? filters.sourceIsin;
    const targetIsin = watch("targetFund")?.isin;
    const sourceFund = watch("sourceFund");
    const targetFund = watch("targetFund");
    const distributor = watch("distributor");

    const hasIsinError = sourceIsin && targetIsin && sourceIsin === targetIsin;

    const onDocumentClick = (isin: string, document: any) =>
    {
        if(!userData.id)
        {
            return;
        }
        DocPreLogService.createDocPreLogs(
        {
            userId: userData.id,
            isin: isin,
            documentType: document.type,
            dateClick: moment().toISOString(),
        }).then((response: DocPreLogs) =>
        {
            setDocPreLogs((prevAux) => [...(prevAux ?? []), response]);
        });
    };

    const renderDocumentColumn = (isin: string, item: any, index: any, column: any) =>
    {
        switch(column.key)
        {
            case "name":
                if(docPreLogs === undefined)
                {
                    return <></>;
                }
                return <span>{item.name}</span>;

            case "link":
                return(
                    <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => onDocumentClick(isin, item)}
                    >
                        <i className={"ironia-icon downloads"} /> Descarga
                    </a>
                );

            case "dateClick":
                if(docPreLogs === undefined)
                {
                    return <></>;
                }
                var docPrelogDateClick = docPreLogs.find((dPL) => dPL.isin === isin && dPL.documentType === item.type);
                if(docPrelogDateClick)
                {
                    return(
                        <T.WithDate>
                            <i className="ironia-icon circledCheck" />
                            <span>
                                {moment(docPrelogDateClick.dateClick).format("DD/MM/YYYY hh:mm")}
                            </span>
                        </T.WithDate>
                    );
                }

                return(
                    <T.WithoutDate>
                        <i className={`ironia-icon cross primaryColor`} />
                        <span>
                            Pulse sobre el enlace de descarga para leer el documento
                        </span>
                    </T.WithoutDate>
                );

            default:
                return item[column.fieldName];
        }
    };

    const loadFundDocuments = async (targetFund: any) =>
    {
        if(targetFund?.isin)
        {
            try
            {
                const targetFundDetails = await fundService.getFund(targetFund.isin);
                setTargetFundDocuments(targetFundDetails.documents || []);
            }
            catch(error)
            {
                console.error("Error loading target fund documents:", error);
                setTargetFundDocuments([]);
            }
        }
        else
        {
            setTargetFundDocuments([]);
        }
    };

    const checkActivePromotions = async (targetFund: any) =>
    {
        if(targetFund?.isin)
        {
            try
            {
                setLoadingPromotions(true);
                const promotions = await PromotionsService.checkActiveIsinPromotion([targetFund.isin]);
                setActivePromotions(promotions);
            }
            catch(error)
            {
                console.error("Error checking active promotions:", error);
                setActivePromotions(null);
            }
            finally
            {
                setLoadingPromotions(false);
            }
        }
        else
        {
            setActivePromotions(null);
            setLoadingPromotions(false);
        }
    };

    const onSubmit = (data: any) =>
    {
        if((data.sourceFund?.name ?? filters.sourceName) === "")
        {
            setError("sourceFund.name", { type: "required" }, { shouldFocus: true });
            return;
        }
        if((data.sourceFund?.isin ?? filters.sourceIsin) === "")
        {
            setError("sourceFund.isin", { type: "required" }, { shouldFocus: true });
            return;
        }
        if(data.amountType === "total" && typeof data.amount !== "undefined")
        {
            delete data.amount;
        }

        // El principal vacío con el que nace todo usuario no identifica a nadie: no se crean órdenes a su nombre
        setSubmitError(undefined);
        const mainUserIdentityId = getUsableMainIdentityId(userData.mainUserIdentity);
        if(mainUserIdentityId === undefined)
        {
            setSubmitError(UnusableMainIdentityOrderMessage);
            return;
        }

        let toSend: any =
        {
            UserIdentityId: mainUserIdentityId,
            FnmPortfolioId: activePlan?.portfolio?.id,
            OrderType: 3,
            Values:
            {
                InceptionFund:
                {
                    Company: data.distributor.name.trim(),
                    Name: (data.sourceFund?.name ?? filters.sourceName).trim(),
                    Isin: (data.sourceFund?.isin ?? filters.sourceIsin).trim(),
                    Account: data.account.trim(),
                    EntityNif: data.distributor.nif.trim(),
                },
                newInstrument: data.targetFund?.finametrixId,
                Fund: data.targetFund?.finametrixId,
                AmountType: data.amountType,
                Amount: data.amount !== undefined ? parseFloat(data.amount) : 0,
                Type: data.amountType,
            },
        };

        setSending(true);
        OrderService.createOrder(toSend).then((response: OrderType) =>
        {
            if(response.id)
            {
                history.push("/transfer/signature",
                {
                    isinOrigin: (data.sourceFund?.isin ?? filters.sourceIsin).trim(),
                    isinDestination: data.targetFund.isin.trim(),
                    signature: response.signature,
                    transfer: response,
                });
            }
            else
            {
                setSubmitError("No se ha podido completar la orden. Por favor, inténtelo de nuevo o contacte con soporte.");
            }
        })
        .catch((error) =>
        {
            if(error.response?.status === 409)
            {
                history.push("/UpgradeSubscription",
                {
                    limitExceeded: error.response.data,
                });
            }
            else if(error.response?.status === 400 && error.response.data?.message)
            {
                setSubmitError(error.response.data.message);
            }
            else
            {
                setSubmitError("No se ha podido crear la orden. Por favor, inténtalo de nuevo.");
            }
        })
        .finally(() =>
        {
            if(mounted.current)
            {
                setSending(false);
            }
        });
    };

    useEffect(() =>
    {
        setSimulationAvailable(hasChangedIsin && sourceFund !== undefined && targetFund !== undefined && distributor !== undefined && !hasIsinError);
    }, [hasChangedExternalManager, hasChangedIsin, sourceFund, targetFund, distributor, hasIsinError, simulationAvailable]);

    useEffect(() =>
    {
        if(targetFund)
        {
            loadFundDocuments(targetFund);
        }

        if(targetFund?.isin && userData.id)
        {
            setLoadingDocPreLogs(true);
            DocPreLogService.docPreLogsByUserId(userData.id, [targetFund.isin]).then((response: DocPreLogs[]) =>
            {
                setDocPreLogs(response);
                setTimeout(() => setLoadingDocPreLogs(false), 100);
            });
        }
        else
        {
            setDocPreLogs([]);
            setLoadingDocPreLogs(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetFund?.isin, userData.id]);

    useEffect(() =>
    {
        let allRead = false;

        if(docPreLogs !== undefined && targetFund && targetFundDocuments.length > 0)
        {
            const docPreLogsTypesByTargetIsin = docPreLogs.filter((dPL: DocPreLogs) => dPL.isin === targetFund.isin).map((dPL: any) => dPL.documentType);
            
            const docsIncluded = checkAllDocsIncluded(targetFund.isin, targetFundDocuments);
            const hasUnreadTarget = !docsIncluded.result || docsIncluded.documents.some((d: any) => !docPreLogsTypesByTargetIsin.includes(d.type));

            if(!hasUnreadTarget)
            {
                allRead = true;
            }
        }

        setAllDocumentsReaded(allRead);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docPreLogs, targetFundDocuments, targetFund?.isin]);

    useEffect(() =>
    {
        if(targetFund && targetFund.isin)
        {
            checkActivePromotions(targetFund);
        }
        else if(targetFund === undefined)
        {
            setActivePromotions(null);
            setLoadingPromotions(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetFund]);

    useEffect(() =>
    {
        if(!targetFund?.isin)
        {
            setTargetFundData(null);
            setTargetFundDataLoading(false);
            return;
        }
        setTargetFundDataLoading(true);
        const isin = targetFund.isin;
        Promise.all([fundService.getPortfolioModelFullInformation([isin]), fundService.getInfoFundsPortfolio([isin])]).then(([navResponse, infoResponse]) =>
        {
            const navList = Array.isArray(navResponse) ? navResponse : [];
            const infoList = Array.isArray(infoResponse) ? infoResponse : [];
            const navItem = navList.find((x: any) => x?.isin === isin) ?? navList[0];
            const infoItem = infoList.find((x: any) => x?.isin === isin) ?? infoList[0];
            const nav = navItem != null && typeof navItem.vLiq === "number"
                    ? navItem.vLiq
                    : 0;
            const ongoingCharges = infoItem != null && typeof infoItem.ongoingCharges === "number"
                    ? infoItem.ongoingCharges
                    : 0;
            setTargetFundData({nav, ongoingCharges});
        })
        .catch(() =>
        {
            setTargetFundData(null);
        })
        .finally(() =>
        {
            setTargetFundDataLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetFund?.isin]);

    useEffect(() =>
    {
        if(!sourceIsin)
        {
            setSourceFundData(null);
            setSourceFundDataLoading(false);
            return;
        }
        setSourceFundDataLoading(true);
        fundService.getPortfolioModelFullInformation([sourceIsin]).then((navResponse) =>
        {
            const navList = Array.isArray(navResponse) ? navResponse : [];
            const navItem = navList.find((x: any) => x?.isin === sourceIsin) ?? navList[0];
            const nav = navItem != null && typeof navItem.vLiq === "number"
                    ? navItem.vLiq
                    : 0;
            setSourceFundData({nav});
        })
        .catch(() =>
        {
            setSourceFundData(null);
        })
        .finally(() =>
        {
            setSourceFundDataLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceFund?.isin, filters?.sourceIsin]);

    const handleSimulation = () =>
    {
        setSimulationLoading(true);
        setSimulation(true);

        if(sourceFund !== undefined && targetFund !== undefined)
        {
            fundService.GetTransferSimulationManagerInfo([sourceFund.isin], [(distributor?.name ?? "").trim()]).then((response: TransferSimulationManagerInfo[]) => setOriginTransferSimulationManagerInfo(response));
            fundService.GetTransferSimulationManagerInfo([targetFund.isin], []).then((response: TransferSimulationManagerInfo[]) => setTargetTransferSimulationManagerInfo(response));
            fundService.getTransferEstimationDates(sourceFund.isin, targetFund.isin, false).then((response: any) =>
            {
                if(response !== null && response !== undefined)
                {
                    setTransferDates(
                    {
                        ventaValor: response.ventaValor,
                        ventaLiquidacion: response.ventaLiquidacion,
                        compraValor: response.compraValor,
                        compraLiquidacion: response.compraLiquidacion,
                        finalProceso: response.finalProceso,
                    });
                }
                else
                {
                    setSimulation(false);
                }
            })
            .finally(() =>
            {
                setSimulationLoading(false);
            });
        }
        else
        {
            setSimulation(false);
        }
    };

    useEffect(() =>
    {
        mounted.current = true;
        return () =>
        {
            mounted.current = false;
        };
    }, []);

    if(outdated === true || cancelled === true)
    {
        return <Redirect to="/savingsPlans" />;
    }
    if(activeManagementPlan === true)
    {
        return <Redirect to="/Portfolio" />;
    }

    return(
        <React.Fragment>
            <DocumentTitle title={"IronIA - Traspaso Externo"}>
                <form
                    className="external-transfer-form"
                    id="external-transfer-form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                >
                    <L.Main theme={theme}>
                        {activePlan !== undefined &&
                        (
                            <div className="ms-Grid-row" style={{ minHeight: "20px" }}>
                                <div className="ms-Grid-col">
                                    <L.TitleContainer>
                                        <h2>Aumenta tu inversión / ahorro</h2>
                                    </L.TitleContainer>
                                </div>
                                <div
                                    className="ms-Grid-col"
                                    style={{ paddingTop: "12px", color: "#CC214F" }}
                                >
                                    <L.SubTitleContainer> {activePlan.name}</L.SubTitleContainer>
                                </div>
                            </div>
                        )}
                        {activePlan === undefined &&
                        (
                            <L.TitleContainer>
                                <h2>Aumenta tu inversión / ahorro</h2>
                            </L.TitleContainer>
                        )}
                        <L.SeparatorTop />
                        {!loadingPromotions &&
                            activePromotions &&
                            (activePromotions as any).TotalPromotionsFound > 0 &&
                            (
                                <T.SectionMargin>
                                    {((activePromotions as any).AvailablePromotions || []).map((promotion: any, index: number) =>
                                    (
                                        <MessageBar
                                            key={`promotion-top-${index}`}
                                            messageBarType={MessageBarType.success}
                                            isMultiline={true}
                                            style={{
                                                marginBottom:
                                                    index <
                                                        (
                                                            (activePromotions as any).AvailablePromotions ||
                                                            []
                                                        ).length -
                                                        1
                                                        ? "10px"
                                                        : "0",
                                            }}
                                        >
                                            <T.PromotionTitle>
                                                ¡Promoción activa! - {promotion.Name} para el ISIN:{" "}
                                                {promotion.ISIN}
                                            </T.PromotionTitle>
                                        </MessageBar>
                                    ))}
                                </T.SectionMargin>
                            )}
                        {loadingPromotions &&
                        (
                            <T.SectionMargin>
                                <MessageBar
                                    messageBarType={MessageBarType.info}
                                    isMultiline={false}
                                >
                                    <T.PromotionsLoadingRow>
                                        <T.SpinnerWithMargin>
                                            <Spinner size={SpinnerSize.small} />
                                        </T.SpinnerWithMargin>
                                        <span>Verificando si hay promociones disponibles...</span>
                                    </T.PromotionsLoadingRow>
                                </MessageBar>
                            </T.SectionMargin>
                        )}
                        <div className="ms-Grid restarted-ms-Grid">
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-xl6">
                                    <L.CardContainer theme={theme}>
                                        <L.CardHeader>
                                            <L.CardHeadingContainer>
                                                <h3>Datos origen</h3>
                                            </L.CardHeadingContainer>
                                            <L.TextSmall className="small-text" theme={theme}>
                                                Si no encuentra el fondo que busca en los desplegables,
                                                puede introducirlo de manera manual en las cajas de
                                                texto correspondientes al ISIN y el nombre del fondo.
                                            </L.TextSmall>
                                        </L.CardHeader>
                                        <L.CardBody>
                                            <Stack>
                                                <Stack.Item>
                                                    <Controller
                                                        control={control}
                                                        name="sourceFund"
                                                        render={({ field, field: { onChange } }) =>
                                                        (
                                                            <InvestmentFundFinder
                                                                {...field}
                                                                labelISIN="ISIN de origen"
                                                                labelName="Nombre del fondo de origen"
                                                                placeholderISIN="Escribe aquí el ISIN de origen..."
                                                                placeholderName="Escribe aquí el nombre del fondo de origen..."
                                                                required={false}
                                                                switchableRequired={true}
                                                                onChange={(ev: any, name: any, value: any) =>
                                                                {
                                                                    if(value !== undefined)
                                                                    {
                                                                        setHasChangedIsin(true);
                                                                        setSimulation(false);
                                                                    }
                                                                    else
                                                                    {
                                                                        setHasChangedIsin(false);
                                                                    }
                                                                    setValue(name, value);
                                                                    onChange({ target: { value, name } });
                                                                }}
                                                                onFilterChange={(inputName: string, value: any) =>
                                                                {
                                                                    setFilters((prevState: filters) =>
                                                                    ({
                                                                        ...prevState,
                                                                        [inputName === "isin"
                                                                            ? "sourceIsin"
                                                                            : "sourceName"]: value,
                                                                    }));
                                                                }}
                                                                errorMessageIsin={
                                                                    errors.sourceFund?.isin &&
                                                                    ((typeof errors.sourceFund.isin.type ===
                                                                        "string" &&
                                                                        errors.sourceFund.isin.type ===
                                                                        "required" &&
                                                                        "Debe seleccionar el isin del fondo de origen.") ||
                                                                        "El fondo de origen no es válido.")
                                                                }
                                                                errorMessageName={
                                                                    errors.sourceFund?.name &&
                                                                    ((typeof errors.sourceFund.name.type ===
                                                                        "string" &&
                                                                        errors.sourceFund.name.type ===
                                                                        "required" &&
                                                                        "Debe seleccionar el nombre del fondo de origen.") ||
                                                                        "El fondo de origen no es válido.")
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </Stack.Item>
                                                <Stack.Item>
                                                    <Controller
                                                        control={control}
                                                        name="distributor"
                                                        rules={{ required: true }}
                                                        render={({ field, field: { onChange } }) =>
                                                        (
                                                            <DistributorFinder
                                                                {...field}
                                                                required
                                                                label="Comercializador"
                                                                placeholder="Escribe aquí el comercializador..."
                                                                onChange={(ev: any, name: any, value: any) =>
                                                                {
                                                                    if(value !== undefined)
                                                                    {
                                                                        setHasChangedExternalManager(true);
                                                                        setSimulation(false);
                                                                    }
                                                                    else
                                                                    {
                                                                        setHasChangedExternalManager(false);
                                                                    }
                                                                    setValue(name, value);
                                                                    onChange({ target: { value, name } });
                                                                }}
                                                                errorMessage=
                                                                {
                                                                    errors.distributor &&
                                                                    ((typeof errors.distributor.type ===
                                                                        "string" &&
                                                                        errors.distributor.type === "required" &&
                                                                        "Debe seleccionar un comercializador.") ||
                                                                        "El comercializador no es válido.")
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </Stack.Item>
                                                <Stack.Item>
                                                    <TextField
                                                        {...register("account",
                                                        {
                                                            required: true,
                                                            minLength: 3,
                                                        })}
                                                        required
                                                        label="Cuenta de origen"
                                                        placeholder="Escribe aquí la cuenta de origen..."
                                                        errorMessage={
                                                            errors.account &&
                                                            ((typeof errors.account.type === "string" &&
                                                                errors.account.type === "required" &&
                                                                "Debe indicar una cuenta de origen.") ||
                                                                "Introduzca una cuenta válida.")
                                                        }
                                                    />
                                                </Stack.Item>
                                            </Stack>
                                            <L.SeparatorForm />
                                            <h3>Tipo de importe</h3>
                                            <Stack>
                                                <Stack.Item>
                                                    <Controller
                                                        name="amountType"
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <ChoiceGroup
                                                                {...field}
                                                                required
                                                                options={amountTypeOptions}
                                                                selectedKey={
                                                                    amountTypeOptions.find(
                                                                        (opt) => opt.value === field.value,
                                                                    )?.key ?? undefined
                                                                }
                                                                onChange={(ev, option) =>
                                                                {
                                                                    if(option)
                                                                    {
                                                                        field.onChange(option.value);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Stack.Item>
                                                {watchAmountType !== "total" && (
                                                    <Stack.Item>
                                                        <Controller
                                                            name="amount"
                                                            control={control}
                                                            rules={{
                                                                required: true,
                                                                min: 0 + Number.MIN_VALUE,
                                                            }}
                                                            render={({ field }) => (
                                                                <TextField
                                                                    {...field}
                                                                    label={
                                                                        (watchAmountType === "titles" &&
                                                                            "Participaciones") ||
                                                                        "Importe"
                                                                    }
                                                                    placeholder={
                                                                        (watchAmountType === "titles" &&
                                                                            "Escribe aquí el número de participaciones...") ||
                                                                        "Escribe aquí el importe..."
                                                                    }
                                                                    type="number"
                                                                    required
                                                                    errorMessage={
                                                                        errors.amount &&
                                                                        ((watchAmountType === "titles" &&
                                                                            ((typeof errors.amount.type ===
                                                                                "string" &&
                                                                                errors.amount.type === "required" &&
                                                                                "Indique las participaciones a traspasar.") ||
                                                                                (typeof errors.amount.type ===
                                                                                    "string" &&
                                                                                    errors.amount.type === "min" &&
                                                                                    "Debe indicar un número mayor de participaciones.") ||
                                                                                "Introduzca un número de participaciones válido.")) ||
                                                                            (typeof errors.amount.type === "string" &&
                                                                                errors.amount.type === "required" &&
                                                                                "Debe indicar el importe a traspasar.") ||
                                                                            (typeof errors.amount.type === "string" &&
                                                                                errors.amount.type === "min" &&
                                                                                "El importe debe ser mayor.") ||
                                                                            "Introduzca un importe válido.")
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Stack.Item>
                                                )}
                                            </Stack>
                                        </L.CardBody>
                                    </L.CardContainer>
                                </div>
                                <div className="ms-Grid-col ms-sm12 ms-xl6">
                                    <L.CardContainer theme={theme}>
                                        <L.CardHeader>
                                            <L.CardHeadingContainer>
                                                <h3>Datos destino</h3>
                                            </L.CardHeadingContainer>
                                        </L.CardHeader>
                                        <L.CardBody>
                                            <Stack>
                                                <Stack.Item>
                                                    <Controller
                                                        name="targetFund"
                                                        control={control}
                                                        rules={
                                                        {
                                                            required: true,
                                                            validate: () =>
                                                                !hasIsinError ||
                                                                "El ISIN de origen y destino no pueden ser el mismo. Si lo que desea es un cambio de comercializador pulse aquí.",
                                                        }}
                                                        render={({ field, field: { onChange } }) =>
                                                        (
                                                            <InvestmentFundFinder
                                                                {...field}
                                                                required
                                                                labelISIN="ISIN de destino"
                                                                labelName="Nombre del fondo de destino"
                                                                placeholderISIN="Escribe aquí el ISIN de destino..."
                                                                placeholderName="Escribe aquí el nombre del fondo de destino..."
                                                                loadData={(filter: any) =>
                                                                {
                                                                    return fundService
                                                                        .getAvailableFunds(filter)
                                                                        .then((data) => data)
                                                                        .catch((err) => []);
                                                                }}
                                                                onChange={(ev: any, name: any, value: any) =>
                                                                {
                                                                    if(value !== undefined)
                                                                    {
                                                                        setHasChangedIsin(true);
                                                                        setSimulation(false);
                                                                    }
                                                                    else
                                                                    {
                                                                        setHasChangedIsin(false);
                                                                    }
                                                                    setValue(name, value);
                                                                    onChange({target: { value, name }});
                                                                }}
                                                                errorMessage={
                                                                    hasIsinError
                                                                        ? "El ISIN de origen y destino no pueden ser el mismo."
                                                                        : errors.targetFund &&
                                                                        (errors.targetFund.type === "required"
                                                                            ? "Debe seleccionar un fondo de destino."
                                                                            : "El fondo de destino no es válido.")
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </Stack.Item>
                                                {hasIsinError &&
                                                (
                                                    <Stack.Item>
                                                        <MessageBar messageBarType={MessageBarType.error}>
                                                            El ISIN de origen y destino no pueden ser el
                                                            mismo. Si lo que desea es un cambio de
                                                            comercializador pulse aquí:{" "}
                                                            <Link href="/distributor-switch">
                                                                Ir a Cambio de Comercializador
                                                            </Link>
                                                        </MessageBar>
                                                    </Stack.Item>
                                                )}
                                            </Stack>
                                            {targetFund &&
                                                (targetFundData != null || targetFundDataLoading) &&
                                                (() =>
                                                {
                                                    if(targetFundDataLoading)
                                                    {
                                                        return(
                                                            <T.LoadingFundsWrapper>
                                                                <Spinner
                                                                    size={SpinnerSize.medium}
                                                                    className="button-spinner"
                                                                />
                                                            </T.LoadingFundsWrapper>
                                                        );
                                                    }
                                                    if(!targetFundData)
                                                    {
                                                        return null;
                                                    }
                                                    const watchAmountVal = watch("amount");
                                                    let operationAmountX: number = 0;
                                                    if(watchAmountType === "total")
                                                    {
                                                        operationAmountX = 0;
                                                    }
                                                    else if(watchAmountType === "amount")
                                                    {
                                                        const amountNum = parseFloat(watchAmountVal);
                                                        if(!isNaN(amountNum) && amountNum > 0)
                                                        {
                                                            operationAmountX = amountNum;
                                                        }
                                                    }
                                                    else if(watchAmountType === "titles")
                                                    {
                                                        const amountNum = parseFloat(watchAmountVal);
                                                        const sourceNav = sourceFundData?.nav ?? 0;
                                                        if(!isNaN(amountNum) && amountNum > 0 && sourceNav > 0)
                                                        {
                                                            operationAmountX = amountNum * sourceNav;
                                                        }
                                                    }
                                                    const nav = targetFundData.nav;
                                                    const ongoingCharges = targetFundData.ongoingCharges;
                                                    const estimatedTitles =
                                                        nav > 0
                                                            ? roundToDecimalPlaces(operationAmountX / nav, 4)
                                                            : 0;
                                                    const ongoingChargesPercent =
                                                        typeof ongoingCharges === "number"
                                                            ? ongoingCharges
                                                            : 0;
                                                    const chargesAmount = operationAmountX * (ongoingChargesPercent / 100);
                                                    return(
                                                        <React.Fragment>
                                                            <T.InstantCalculationTitle>
                                                                Cálculo Estimado
                                                            </T.InstantCalculationTitle>
                                                            <T.EstimatedCalculationIntroText>
                                                                Estos valores se realizan estimando el cálculo
                                                                al tomar el último valor del precio del fondo de
                                                                destino.
                                                            </T.EstimatedCalculationIntroText>
                                                            {watchAmountType === "titles" &&
                                                                sourceFundDataLoading && (
                                                                    <T.EstimatedCalculationIntroText>
                                                                        Cargando datos del fondo de origen para el
                                                                        cálculo por participaciones...
                                                                    </T.EstimatedCalculationIntroText>
                                                                )}
                                                            <T.CardsRowWrapper>
                                                                <T.InternalSwitchCardContainer theme={theme}>
                                                                    <T.InternalSwitchCardTitle>
                                                                        Importe
                                                                    </T.InternalSwitchCardTitle>
                                                                    <T.InternalSwitchCardContent>
                                                                        {currencyFormatter.format(operationAmountX)}
                                                                    </T.InternalSwitchCardContent>
                                                                </T.InternalSwitchCardContainer>
                                                                <T.InternalSwitchCardContainer theme={theme}>
                                                                    <T.InternalSwitchCardTitle>
                                                                        Títulos / Participaciones
                                                                    </T.InternalSwitchCardTitle>
                                                                    <T.InternalSwitchCardContent>
                                                                        {decimalFormatter.format(estimatedTitles)}
                                                                    </T.InternalSwitchCardContent>
                                                                    <T.InternalSwitchCardFooter>
                                                                        * Estimadas en destino
                                                                    </T.InternalSwitchCardFooter>
                                                                </T.InternalSwitchCardContainer>
                                                                <T.InternalSwitchCardContainer theme={theme}>
                                                                    <T.InternalSwitchCardTitle>
                                                                        Gastos Corrientes
                                                                    </T.InternalSwitchCardTitle>
                                                                    <T.InternalSwitchCardContent>
                                                                        {ongoingChargesPercent > 0
                                                                            ? `${decimalFormatter.format(ongoingChargesPercent)} %`
                                                                            : "-"}
                                                                    </T.InternalSwitchCardContent>
                                                                    <T.InternalSwitchCardFooter>
                                                                        {ongoingChargesPercent > 0
                                                                            ? `* Equivale a ${currencyFormatter.format(chargesAmount)}`
                                                                            : "* Del fondo destino"}
                                                                    </T.InternalSwitchCardFooter>
                                                                </T.InternalSwitchCardContainer>
                                                            </T.CardsRowWrapper>
                                                        </React.Fragment>
                                                    );
                                                })()}
                                        </L.CardBody>
                                    </L.CardContainer>
                                </div>
                            </div>
                        </div>
                        {targetFund && targetFundData && !targetFundDataLoading &&
                        (
                            <T.CardsDisclaimerWrapper theme={theme}>
                                <T.CardsDisclaimerText>
                                    Los datos del Cálculo Estimado son orientativos y no tienen
                                    carácter vinculante legalmente para reclamación ni reflejan
                                    necesariamente los valores finales una vez ejecutada la
                                    operación en el mercado.
                                </T.CardsDisclaimerText>
                            </T.CardsDisclaimerWrapper>
                        )}
                        <L.OuterFooterArea>
                            <L.CardButton
                                size="lg"
                                type="button"
                                onClick={() => history.goBack()}
                            >
                                Volver
                            </L.CardButton>
                            <L.FooterRightButtons>
                                <L.CardButton
                                    size="lg"
                                    disabled={!simulationAvailable}
                                    type="button"
                                    style={{ marginRight: "1em" }}
                                    onClick={() =>
                                    {
                                        setSimulationLoading(true);
                                        handleSimulation();
                                    }}
                                >
                                    Simular
                                </L.CardButton>
                                <L.CardButton
                                    size="lg"
                                    disabled={sending || !allDocumentsReaded || hasIsinError}
                                >
                                    {sending &&
                                    (
                                        <Spinner
                                            size={SpinnerSize.small}
                                            className="button-spinner"
                                        />
                                    )}
                                    Continuar
                                </L.CardButton>
                            </L.FooterRightButtons>
                        </L.OuterFooterArea>
                        {submitError !== undefined &&
                        (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={true}
                            >
                                {submitError}
                            </MessageBar>
                        )}
                        {simulation === true &&
                            simulationAvailable === true &&
                            transferDates !== undefined &&
                            (
                                <TransferTimeLine
                                    isLoadingProps={simulationLoading}
                                    transferEstimationDates={transferDates}
                                    simulationManagerInfoTarget={
                                        targetTransferSimulationManagerInfo
                                    }
                                    simulationManagerInfoOrigin={
                                        originTransferSimulationManagerInfo
                                    }
                                />
                            )}
                        {!loadingDocPreLogs && !allDocumentsReaded && 
                        (
                            <T.DocumentsContainer theme={theme}>
                                <T.DocumentsTitle>
                                    Documentación obligatoria de IIC's
                                </T.DocumentsTitle>
                                <T.DocumentsIntroText>
                                    De conformidad con lo establecido en la normativa vigente
                                    aplicable a Instituciones de Inversión Colectiva y en las
                                    Circulares 4/2008 y 2/2011 de la Comisión Nacional del Mercado
                                    de Valores, debe leer la documentación legal requerida antes
                                    de realizar el traspaso.
                                </T.DocumentsIntroText>
                                <div className={`ms-Grid-row ${styles.documents}`}>
                                    {targetFund &&
                                        (() =>
                                        {
                                            const allDocsReaded = checkAllDocsIncluded(
                                                targetFund.isin,
                                                targetFundDocuments,
                                            );
                                            return(
                                                <div className={`ms-Grid-col ms-lg12 ms-xl6 p10`}>
                                                    <T.DocumentsSubtitle>
                                                        Documentos del fondo destino - {targetFund.isin}
                                                    </T.DocumentsSubtitle>
                                                    {!allDocsReaded.result ?
                                                    (
                                                        <p className={`shadow ${styles.noDocuments}`}>
                                                            <i
                                                                className={`ironia-icon cross inlineBlock ${styles.cross}`}
                                                            />
                                                            <span>
                                                                No hay documentación disponible para este fondo
                                                                por lo que no se puede usar como destino de un
                                                                traspaso.
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <DetailsList
                                                            setKey="targetDocs"
                                                            items={allDocsReaded.documents}
                                                            columns={documentsColumns}
                                                            onRenderItemColumn={(row, index, column) =>
                                                                renderDocumentColumn(
                                                                    targetFund.isin,
                                                                    row,
                                                                    index,
                                                                    column,
                                                                )
                                                            }
                                                            selectionMode={SelectionMode.none}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })()}
                                </div>
                            </T.DocumentsContainer>
                        )}
                        {loadingDocPreLogs &&
                        (
                            <T.DocumentsContainer theme={theme}>
                                <T.LoadingDocsContainer>
                                    <Spinner size={SpinnerSize.medium} />
                                    <T.LoadingDocsText>
                                        Cargando documentación...
                                    </T.LoadingDocsText>
                                </T.LoadingDocsContainer>
                            </T.DocumentsContainer>
                        )}
                    </L.Main>
                </form>
            </DocumentTitle>
        </React.Fragment>
    );
};